'use client';

import { useState, useEffect, useRef } from 'react';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import LiveSearchList from './LiveSearchList';
import { usePathname, useRouter } from 'next/navigation';
import { pushToDataLayer } from '@/features/gtm/gtmUtils';
import { useFetcherCSR } from '@/lib/useFetcherCSR';
import { API_GIGALOGY_SEARCH_QUERY } from '@/constant/apiRout';

const SearchComponent = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchRef = useRef<HTMLDivElement>(null);

  // -----------------------------
  // STATE
  // -----------------------------
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // -----------------------------
  // API CALL
  // -----------------------------
  const { data, isLoading } = useFetcherCSR(API_GIGALOGY_SEARCH_QUERY(search));

  // -----------------------------
  // FIX: DYNAMIC OVERFLOW UNLOCK
  // -----------------------------
  useEffect(() => {
    // Finds the closest parent that might have overflow:hidden
    const parentContainer = searchRef.current?.closest('.header-nav-container, .header-bottom');

    if (showSuggestions && search.length > 0) {
      parentContainer?.classList.add('is-searching');
    } else {
      parentContainer?.classList.remove('is-searching');
    }

    return () => parentContainer?.classList.remove('is-searching');
  }, [showSuggestions, search]);

  // -----------------------------
  // CLICK OUTSIDE TO CLOSE
  // ----------------------------- 
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSearch('');
    setShowSuggestions(false);
  }, [pathname]);

  const handleClearSuggestions = () => {
    setSearch('');
    setShowSuggestions(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    const searchTerm = search.trim();
    if (searchTerm) {
      pushToDataLayer('search', { search_term: searchTerm });
      router.push(`/shop?search=${encodeURIComponent(searchTerm)}`);
      setSearch('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setShowSuggestions(true);
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div ref={searchRef} className="search-module position-relative">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="search-bar d-flex">
        <input
          type="text"
          className="search-bar__input flex-grow-1"
          placeholder="Search for product"
          value={search}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
        />
        <div className="search-bar__search-buttons">
          <button className="bg-transparent border-0 shadow-none" type="submit">
            <HugeiconsIcon icon={Search01Icon} />
          </button>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && search.length > 0 && (
        <div className="search-results">
          {isLoading ? (
            <div className="search">
              <div className="search__item">
                <div className="search__name p-3">Loading...</div>
              </div>
            </div>
          ) : data?.data?.length > 0 ? (
            <LiveSearchList
              suggestions={data?.data || []}
              clearSuggestions={handleClearSuggestions}
            />
          ) : (
            <div className="search">
              <div className="search__item">
                <div className="search__name p-3">No product found!</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;